import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import config from '../../config';
import {
    TRANSITIONS,
    FILE_MESSAGE_TEXT_CONTENT,
    isContactInfo,
    APPOINTMENT_DECLINED_STATUS,
    APPOINTMENT_CANCELED_STATUS,
    APPOINTMENT_ACCEPTED_STATUS,
    DECLINE_APPOINTMENT_REQUEST,
    CANCEL_APPOINTMENT_REQUEST,
    ACCEPT_APPOINTMENT_REQUEST,
} from '../../util/transaction';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities, ensureCurrentUser } from '../../util/data';
import { updateUserProfileInfo } from '../../ducks/user.duck';
import {
    createAppointment,
    deleteTransactionMessage,
    getAppointment,
    getTransactionMessages,
    sendTransactionMessageFile,
    sendTxSGEmail,
    updateAppointment,
} from '../../util/api';
import { queryTransactions } from '../../ducks/SdkDataAccessLayer.duck';

const { userTypeHorseowner, userTypeRider } = config;
const appointmentStatusMapper = {
    [ACCEPT_APPOINTMENT_REQUEST]: { status: APPOINTMENT_ACCEPTED_STATUS },
    [DECLINE_APPOINTMENT_REQUEST]: { status: APPOINTMENT_DECLINED_STATUS },
    [CANCEL_APPOINTMENT_REQUEST]: { status: APPOINTMENT_CANCELED_STATUS },
};

const DELETED_MESSAGE_CONTENT = 'Diese Nachricht wurde gelöscht.';

const applyDeletedMessagesContent = (messages, deletedMessages) =>
    deletedMessages
        ? messages.map(message =>
              deletedMessages[message.id.uuid]
                  ? {
                        ...message,
                        attributes: {
                            ...message.attributes,
                            content: DELETED_MESSAGE_CONTENT,
                            isDeleted: true,
                        },
                    }
                  : message
          )
        : messages;

const sortedTransactions = txs =>
    reverse(
        sortBy(txs, tx => {
            return tx.attributes ? tx.attributes.lastTransitionedAt : null;
        })
    );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const ARCHIEVE_TRANSACTION_REQUEST = 'app/InboxPage/ARCHIEVE_TRANSACTION_REQUEST';
export const ARCHIEVE_TRANSACTION_SUCCESS = 'app/InboxPage/ARCHIEVE_TRANSACTION_SUCCESS';
export const ARCHIEVE_TRANSACTION_ERROR = 'app/InboxPage/ARCHIEVE_TRANSACTION_ERROR';

export const FETCH_SINGLE_CHAT_MESSAGES_REQUEST =
    'app/InboxPage/FETCH_SINGLE_CHAT_MESSAGES_REQUEST';
export const FETCH_SINGLE_CHAT_MESSAGES_SUCCESS =
    'app/InboxPage/FETCH_SINGLE_CHAT_MESSAGES_SUCCESS';
export const FETCH_SINGLE_CHAT_MESSAGES_ERROR = 'app/InboxPage/FETCH_SINGLE_CHAT_MESSAGES_ERROR';

export const FETCH_INBOX_MESSAGES_REQUEST = 'app/InboxPage/FETCH_INBOX_MESSAGES_REQUEST';
export const FETCH_INBOX_MESSAGES_SUCCESS = 'app/InboxPage/FETCH_INBOX_MESSAGES_SUCCESS';
export const FETCH_INBOX_MESSAGES_ERROR = 'app/InboxPage/FETCH_INBOX_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/InboxPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/InboxPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/InboxPage/SEND_MESSAGE_ERROR';

export const SEND_FILE_REQUEST = 'app/InboxPage/SEND_FILE_REQUEST';
export const SEND_FILE_SUCCESS = 'app/InboxPage/SEND_FILE_SUCCESS';
export const SEND_FILE_ERROR = 'app/InboxPage/SEND_FILE_ERROR';

export const DELETE_MESSAGE_REQUEST = 'app/InboxPage/DELETE_MESSAGE_REQUEST';
export const DELETE_MESSAGE_SUCCESS = 'app/InboxPage/DELETE_MESSAGE_SUCCESS';
export const DELETE_MESSAGE_ERROR = 'app/InboxPage/DELETE_MESSAGE_ERROR';

export const SET_APPOINTMENT_FETCH_ERROR = 'app/InboxPage/SET_APPOINTMENT_FETCH_ERROR';
export const SET_APPOINTMENT_FETCH_SUCCESS = 'app/InboxPage/SET_APPOINTMENT_FETCH_SUCCESS';
export const SET_APPOINTMENT_FETCH_IN_PROGRESS = 'app/InboxPage/SET_APPOINTMENT_FETCH_IN_PROGRESS';
export const INIT_APPOINTMENT_ENTITIES = 'app/InboxPage/INIT_APPOINTMENT_ENTITIES';
export const SEND_APPOINTMENT_REQUEST = 'app/InboxPage/SEND_APPOINTMENT_REQUEST';
export const SEND_APPOINTMENT_SUCCESS = 'app/InboxPage/SEND_APPOINTMENT_SUCCESS';
export const SEND_APPOINTMENT_UPDATE_SUCCESS = 'app/InboxPage/SEND_APPOINTMENT_UPDATE_SUCCESS';
export const SEND_APPOINTMENT_ERROR = 'app/InboxPage/SEND_APPOINTMENT_ERROR';
export const ADD_APPOINTMENT_ENTITY = 'app/InboxPage/ADD_APPOINTMENT_ENTITY';
export const MERGE_APPOINTMENT_ENTITY = 'app/InboxPage/MERGE_APPOINTMENT_ENTITY';

// ================ Reducer ================ //

const entityRefs = entities =>
    entities.map(entity => ({
        id: entity.id,
        type: entity.type,
    }));

const initialState = {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
    archievedTransactionsError: false,
    archievedTransactionsLoading: false,
    fetchMessagesError: null,
    allUserMessages: {},
    allUserMessagesRequestInProgress: false,
    allUserMessagesError: null,
    totalMessages: 0,
    totalMessagePages: 0,
    oldestMessagePageFetched: 0,
    initialMessageFailedToTransaction: null,
    sendMessageError: null,
    sendFileError: null,
    fetchMessagesInProgress: false,
    sendMessageInProgress: false,
    sendFileInProgress: false,
    deleteMessageInProgress: false,
    deleteMessageError: null,
    deleteMessagePendingId: null,
    appointmentFetchError: null,
    appointmentFetchInProgress: false,
    appointmentEntities: {},
    appointmentError: null,
    sendAppointmentInProgress: false,
};

const mergeEntityArrays = (a, b) =>
    a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);

export default function checkoutPageReducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case FETCH_ORDERS_OR_SALES_REQUEST:
            return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
        case FETCH_ORDERS_OR_SALES_SUCCESS: {
            const transactions = sortedTransactions(payload.data.data);

            return {
                ...state,
                fetchInProgress: false,
                transactionRefs: entityRefs(transactions),
                pagination: payload.data.meta,
            };
        }
        case FETCH_ORDERS_OR_SALES_ERROR:
            return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
        case ARCHIEVE_TRANSACTION_REQUEST:
            return {
                ...state,
                archievedTransactionsLoading: true,
                archievedTransactionsError: null,
            };
        case ARCHIEVE_TRANSACTION_SUCCESS:
            return {
                ...state,
                archievedTransactionsLoading: false,
                archievedTransactionsError: null,
            };
        case ARCHIEVE_TRANSACTION_ERROR:
            return {
                ...state,
                archievedTransactionsLoading: false,
                fetchOrdersOrSalesError: payload,
            };
        case FETCH_SINGLE_CHAT_MESSAGES_REQUEST:
            return {
                ...state,
                totalMessages: 0,
                totalMessagePages: 0,
                oldestMessagePageFetched: 0,
                fetchMessagesInProgress: true,
                fetchMessagesError: null,
            };
        case FETCH_SINGLE_CHAT_MESSAGES_SUCCESS:
            return {
                ...state,
                fetchMessagesInProgress: false,
                allUserMessages: {
                    ...state.allUserMessages,
                    [payload.transactionId]: mergeEntityArrays(
                        state.allUserMessages[payload.transactionId] || [],
                        payload.messages
                    ),
                },
                totalMessages: payload.totalItems,
                totalMessagePages: payload.totalPages,
                oldestMessagePageFetched: Math.max(state.oldestMessagePageFetched, payload.page),
            };
        case FETCH_SINGLE_CHAT_MESSAGES_ERROR:
            return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };
        case FETCH_INBOX_MESSAGES_REQUEST:
            return {
                ...state,
                allUserMessagesRequestInProgress: true,
                allUserMessagesError: null,
            };
        case FETCH_INBOX_MESSAGES_SUCCESS:
            return {
                ...state,
                allUserMessages: payload,
                allUserMessagesRequestInProgress: false,
                allUserMessagesError: null,
            };
        case FETCH_INBOX_MESSAGES_ERROR:
            return {
                ...state,
                allUserMessagesRequestInProgress: false,
                allUserMessagesError: payload,
            };
        case SEND_MESSAGE_REQUEST:
            return {
                ...state,
                sendMessageInProgress: true,
                sendMessageError: null,
                initialMessageFailedToTransaction: null,
            };
        case SEND_MESSAGE_SUCCESS:
            return { ...state, sendMessageInProgress: false };
        case SEND_MESSAGE_ERROR:
            return { ...state, sendMessageInProgress: false, sendMessageError: payload };
        case SEND_FILE_REQUEST:
            return { ...state, sendFileInProgress: true, sendFileError: null };
        case SEND_FILE_SUCCESS:
            return { ...state, sendFileInProgress: false };
        case SEND_FILE_ERROR:
            return { ...state, sendFileInProgress: false, sendFileError: payload };
        case DELETE_MESSAGE_REQUEST:
            return {
                ...state,
                deleteMessageInProgress: true,
                deleteMessageError: null,
                deleteMessagePendingId: payload,
            };
        case DELETE_MESSAGE_SUCCESS:
            return {
                ...state,
                deleteMessageInProgress: false,
                deleteMessageError: null,
                deleteMessagePendingId: null,
                allUserMessages: {
                    ...state.allUserMessages,
                    [payload.transactionId]: applyDeletedMessagesContent(
                        state.allUserMessages[payload.transactionId] || [],
                        { [payload.messageId]: true }
                    ),
                },
            };
        case DELETE_MESSAGE_ERROR:
            return {
                ...state,
                deleteMessageInProgress: false,
                deleteMessageError: payload,
            };
        case SET_APPOINTMENT_FETCH_ERROR:
            return { ...state, appointmentFetchError: payload };
        case SET_APPOINTMENT_FETCH_IN_PROGRESS:
            return { ...state, appointmentFetchError: null, appointmentFetchInProgress: true };
        case SET_APPOINTMENT_FETCH_SUCCESS:
            return { ...state, appointmentFetchError: null, appointmentFetchInProgress: false };
        case INIT_APPOINTMENT_ENTITIES:
            return {
                ...state,
                appointmentEntities: {
                    ...state.appointmentEntities,
                    [payload.transactionId]: [...payload.entities],
                },
            };
        case SEND_APPOINTMENT_REQUEST:
            return { ...state, sendAppointmentInProgress: true, appointmentError: null };
        case SEND_APPOINTMENT_SUCCESS:
        case SEND_APPOINTMENT_UPDATE_SUCCESS:
            return { ...state, sendAppointmentInProgress: false, appointmentError: null };
        case SEND_APPOINTMENT_ERROR:
            return { ...state, sendAppointmentInProgress: false, appointmentError: payload };
        case ADD_APPOINTMENT_ENTITY:
            return {
                ...state,
                appointmentEntities: {
                    ...state.appointmentEntities,
                    [payload.transactionId]: [
                        ...(state.appointmentEntities[payload.transactionId] || []),
                        payload.entity,
                    ],
                },
            };
        case MERGE_APPOINTMENT_ENTITY:
            return {
                ...state,
                appointmentEntities: {
                    ...state.appointmentEntities,
                    [payload.transactionId]: (
                        state.appointmentEntities[payload.transactionId] || []
                    ).map(entity => (entity._id === payload._id ? payload : entity)),
                },
            };

        default:
            return state;
    }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
    type: FETCH_ORDERS_OR_SALES_SUCCESS,
    payload: response,
});
const fetchOrdersOrSalesError = e => ({
    type: FETCH_ORDERS_OR_SALES_ERROR,
    error: true,
    payload: e,
});

const requestArchievedTransactionData = () => ({
    type: ARCHIEVE_TRANSACTION_REQUEST,
});

const requestArchievedTransactionSuccess = () => ({
    type: ARCHIEVE_TRANSACTION_SUCCESS,
});

const requestArchievedTransactionFailure = payload => ({
    type: ARCHIEVE_TRANSACTION_ERROR,
    payload,
});

const fetchSingleChatMessagesRequest = () => ({ type: FETCH_SINGLE_CHAT_MESSAGES_REQUEST });
const fetchSingleChatMessagesSuccess = (transactionId, messages, pagination) => ({
    type: FETCH_SINGLE_CHAT_MESSAGES_SUCCESS,
    payload: { transactionId, messages, ...pagination },
});
const fetchSingleChatMessagesError = error => ({
    type: FETCH_SINGLE_CHAT_MESSAGES_ERROR,
    error: true,
    payload: error,
});

const fetchInboxMessagesRequest = () => ({ type: FETCH_INBOX_MESSAGES_REQUEST });
const fetchInboxMessagesSuccess = messages => ({
    type: FETCH_INBOX_MESSAGES_SUCCESS,
    payload: messages,
});
const fetchInboxMessagesError = error => ({
    type: FETCH_INBOX_MESSAGES_ERROR,
    payload: error,
});

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = transactionId => ({
    type: SEND_MESSAGE_SUCCESS,
    payload: transactionId,
});
const sendMessageError = error => ({ type: SEND_MESSAGE_ERROR, error: true, payload: error });

const sendFileRequest = () => ({ type: SEND_FILE_REQUEST });
const sendFileSuccess = transactionId => ({
    type: SEND_FILE_SUCCESS,
    payload: transactionId,
});
const sendFileError = error => ({ type: SEND_FILE_ERROR, error: true, payload: error });

const deleteMessageRequest = messageId => ({ type: DELETE_MESSAGE_REQUEST, payload: messageId });
const deleteMessageSuccess = (transactionId, messageId) => ({
    type: DELETE_MESSAGE_SUCCESS,
    payload: { transactionId, messageId },
});
const deleteMessageError = error => ({ type: DELETE_MESSAGE_ERROR, error: true, payload: error });

const setAppointmentFetchSuccess = () => ({ type: SET_APPOINTMENT_FETCH_SUCCESS });
const setAppointmentFetchInProgress = () => ({ type: SET_APPOINTMENT_FETCH_IN_PROGRESS });
const setAppointmentFetchError = error => ({
    type: SET_APPOINTMENT_FETCH_ERROR,
    payload: error,
});
const initAppointmentEntities = payload => ({ type: INIT_APPOINTMENT_ENTITIES, payload });
const sendAppointmentRequest = () => ({ type: SEND_APPOINTMENT_REQUEST });
const sendAppointmentSuccess = transactionId => ({
    type: SEND_APPOINTMENT_SUCCESS,
    payload: transactionId,
});
const updateAppointmentSuccess = transactionId => ({
    type: SEND_APPOINTMENT_UPDATE_SUCCESS,
    payload: transactionId,
});
const sendAppointmentError = error => ({ type: SEND_APPOINTMENT_ERROR, payload: error });
const addAppointmentEntity = payload => ({ type: ADD_APPOINTMENT_ENTITY, payload });
const mergeAppointmentEntity = payload => ({ type: MERGE_APPOINTMENT_ENTITY, payload });

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;
const MESSAGES_PAGE_SIZE = 100;
const IMAGE_VARIANTS = {
    'fields.image': [
        'variants.square-small',
        'variants.square-small2x',
        'variants.landscape-crop',
        'variants.landscape-crop2x',
    ],
};
/**
 * txDataApiQueryParams for initial data load
 */
export const txDataApiQueryParams = {
    // only: onlyFilter,
    lastTransitions: TRANSITIONS,
    include: [
        'provider',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
        'listing',
    ],
    'fields.transaction': [
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'protectedData',
        'metadata',
    ],
    'fields.user': [
        'banned',
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.bio',
        'profile.publicData.country', // rider + ho
        'profile.publicData.chatLastActivity', // rider + ho
        'profile.publicData.userType', // rider + ho
        'profile.publicData.extrapackagesData', // rider only
        'profile.publicData.anonymousListingTitle', // rider only
        'profile.publicData.deanonymizedFor', // rider only
        'profile.publicData.availabilityStatus', // rider only
        'profile.publicData.hasDefaultAvatar', // rider only,
        'profile.publicData.contactPreference',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

export const fetchTransactionsData = (params = {}) => async (dispatch, getState, sdk) =>
    dispatch(queryTransactions(params))
        .then(response => response)
        .catch(e => {
            throw e;
        });

export const fetchSubstitutedListings = transactions => (dispatch, getState, sdk) =>
    Promise.all(
        transactions.map(async transaction => {
            const { listingSubstitutionId } = transaction.attributes.protectedData || {};

            if (!listingSubstitutionId) {
                return transaction;
            }

            const listing = await sdk.listings.query({
                ids: [listingSubstitutionId],
            });
            dispatch(addMarketplaceEntities(listing));

            return {
                ...transaction,
                relationships: {
                    ...transaction.relationships,
                    listing: {
                        ...transaction.relationships.listing,
                        data: listing.data.data[0],
                    },
                },
            };
        })
    );

export const fetchFiles = (txId, messages) => async () => {
    try {
        const response = await getTransactionMessages(txId.uuid);
        const data = await response.json();
        const files = data && data.data && data.data.data;

        if (files && messages) {
            messages.forEach(message => {
                const file = files.find(item => item.id.uuid === message.id.uuid);
                if (file && file.attributes && file.attributes.file) {
                    message.attributes.file = file.attributes.file;
                }
            });
        }
    } catch (error) {
        console.error('The following error occured at fetchSingleChatMessages method: ', error);
    }
};

export const prepareAndSendSGMessage = ({ txId, message }) => async (_, getState) => {
    const { currentUser } = getState().user;
    const currentUserIsOwner =
        currentUser.attributes.profile.publicData.userType === userTypeHorseowner;
    const templateId = currentUserIsOwner
        ? 'transactionNewMessageRider'
        : 'transactionNewMessageHorseowner';
    const contactInfoContentParsed = isContactInfo({ attributes: { content: message } }, true);

    await sendTxSGEmail(
        JSON.stringify({
            message:
                typeof contactInfoContentParsed === 'object' ? 'Geteilte Mobilnummer' : message,
            transactionId: txId,
            templateId,
            senderId: currentUser.id.uuid,
        })
    );
};

/**
 * Fetches the first message page for every inbox transaction and groups the
 * results by transaction ID for inbox previews and unread-message checks.
 */
export const fetchInboxMessages = transactions => dispatch => {
    dispatch(fetchInboxMessagesRequest());

    return Promise.all(
        transactions.map(async ({ id }) => {
            const response = await dispatch(fetchSingleChatMessages(id, 1));
            return { id, data: response };
        })
    )
        .then(response => {
            const messages = response.reduce(
                (acc, { id: { uuid }, data }) => ({ ...acc, [uuid]: data }),
                {}
            );
            dispatch(fetchInboxMessagesSuccess(messages));
            return messages;
        })
        .catch(error => {
            dispatch(fetchInboxMessagesError(error));
            return null;
        });
};

/**
 * Fetches one paginated message page for a single transaction chat.
 * The result is merged into the transaction's entry in allUserMessages.
 */
export const fetchSingleChatMessages = (txId, page) => (dispatch, getState, sdk) => {
    if (!txId?.uuid) return Promise.resolve();

    dispatch(fetchSingleChatMessagesRequest());

    return sdk.messages
        .query({
            transaction_id: txId,
            include: ['sender', 'sender.profileImage'],
            ...IMAGE_VARIANTS,
            page,
            per_page: MESSAGES_PAGE_SIZE,
        })
        .then(async response => {
            const messages = denormalisedResponseEntities(response);
            await dispatch(fetchFiles(txId, messages));
            const { totalItems, totalPages, page: fetchedPage } = response.data.meta;

            const [transaction] = getMarketplaceEntities(getState(), [
                { id: txId, type: 'transaction' },
            ]);
            const deletedMessages = transaction?.attributes?.metadata?.deletedMessages;
            const messagesWithDeletedContent = applyDeletedMessagesContent(
                messages,
                deletedMessages
            );

            dispatch(
                fetchSingleChatMessagesSuccess(txId.uuid, messagesWithDeletedContent, {
                    totalItems,
                    totalPages,
                    page: fetchedPage,
                })
            );

            return messagesWithDeletedContent;
        })
        .catch(error => {
            dispatch(fetchSingleChatMessagesError(storableError(error)));
            throw error;
        });
};

export const sendMessage = (txId, message) => (dispatch, getState, sdk) => {
    dispatch(sendMessageRequest());

    return sdk.messages
        .send({ transactionId: txId, content: message })
        .then(response => {
            dispatch(prepareAndSendSGMessage({ txId: txId.uuid, message }));
            return dispatch(fetchSingleChatMessages(txId, 1)).then(() => {
                dispatch(sendMessageSuccess(txId));
                return response.data.data.id;
            });
        })
        .catch(error => {
            dispatch(sendMessageError(storableError(error)));
            throw error;
        });
};

export const sendFile = (transaction, file) => dispatch => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('content', FILE_MESSAGE_TEXT_CONTENT);
    const fileIsImage = /image/.test(file?.type);
    dispatch(sendFileRequest());

    return sendTransactionMessageFile(transaction.id.uuid, formData)
        .then(response => {
            if (response?.error) {
                const err = new Error(response?.message || 'Unknown error');
                Object.assign(err, {
                    code: response?.code || 'unknown',
                });

                throw err;
            }

            dispatch(
                prepareAndSendSGMessage({
                    txId: transaction.id.uuid,
                    message: fileIsImage ? 'Foto' : 'Dokument',
                })
            );

            dispatch(fetchSingleChatMessages(transaction.id, 1)).then(() => {
                dispatch(sendFileSuccess(transaction.id));
                return response;
            });
        })
        .catch(error => {
            error.type = 'error';
            dispatch(sendFileError(error));
        });
};

export const deleteMessage = (transactionId, messageId) => async (dispatch, getState) => {
    try {
        dispatch(deleteMessageRequest(messageId));

        const response = await deleteTransactionMessage(messageId, transactionId);

        const defaultErrorMessage =
            'Nachricht konnte nicht gelöscht werden. Bitte versuche es nochmals.';

        if (response.error) {
            return dispatch(deleteMessageError(response.message || defaultErrorMessage));
        }

        const transactionsData = await response.json();

        if (transactionsData.error) {
            return dispatch(deleteMessageError(transactionsData.message || defaultErrorMessage));
        }

        dispatch(addMarketplaceEntities(transactionsData));
        dispatch(deleteMessageSuccess(transactionId, messageId));

        return transactionsData;
    } catch (error) {
        dispatch(deleteMessageError(storableError(error)));
    }
};

export const fetchAppointments = transactions => dispatch => {
    dispatch(setAppointmentFetchInProgress());

    return Promise.all(
        transactions.map(async ({ id: { uuid } }) => {
            const response = await getAppointment(uuid);
            const { error, message, appointments, transitions } = await response.json();
            if (error) {
                dispatch(setAppointmentFetchError(message));
                return;
            }
            dispatch(
                initAppointmentEntities({
                    transactionId: uuid,
                    entities: [...appointments, ...transitions],
                })
            );
        })
    )
        .then(() => dispatch(setAppointmentFetchSuccess()))
        .catch(error => dispatch(setAppointmentFetchError(error.message)));
};

export const sendAppointment = data => async dispatch => {
    const { otherParty, currentUser, currentListing, intl, ...appointmentData } = data;

    dispatch(sendAppointmentRequest());
    const defaultErrorMessage = 'Failed to send a message for appointment.';

    return createAppointment(JSON.stringify(appointmentData))
        .then(response => {
            const { error, message } = response;

            if (error) {
                throw new Error(message || defaultErrorMessage);
            }

            const { transactionId } = appointmentData;
            dispatch(sendAppointmentSuccess({ uuid: transactionId }));
            dispatch(addAppointmentEntity({ transactionId, entity: response }));
            return {};
        })
        .catch(error => dispatch(sendAppointmentError(error.message)));
};

export const proceedWithAppointment = (data, action) => dispatch => {
    const { appointmentData } = data;
    const { transactionId } = appointmentData;
    const actionMaybe = appointmentStatusMapper[action];

    if (!action || !actionMaybe || !actionMaybe.status) {
        throw new Error(`Unexpected transaction action ${action}.`);
    }
    if (!transactionId) {
        throw new Error(`Unexpected transaction id ${transactionId}.`);
    }

    dispatch(sendAppointmentRequest());

    return updateAppointment({ transactionId, statusUpdated: actionMaybe.status })
        .then(response => response.json())
        .then(response => {
            const { error, message, appointment, transition } = response;

            if (error) throw new Error(message);
            if (!appointment || !transition) {
                const failedData = !appointment ? 'appointment.data' : 'transition.data';
                throw new Error(`Some data failed to update: ${failedData}.`);
            }

            dispatch(
                mergeAppointmentEntity({ transactionId, _id: appointment._id, ...appointment })
            );
            dispatch(updateAppointmentSuccess({ uuid: transactionId }));
            dispatch(addAppointmentEntity({ transactionId, entity: transition }));
            return {};
        })
        .catch(error => dispatch(sendAppointmentError(error.message)));
};

const filterTxByRole = (response, user) => {
    const {
        id,
        attributes: {
            profile: {
                publicData: { userType },
            },
        },
    } = user;

    const currentUserId = id.uuid;

    if (!currentUserId) {
        return response;
    }

    response.data.data = response.data.data.reduce((acc, tx) => {
        const {
            attributes: { protectedData },
            relationships: {
                provider: {
                    data: {
                        id: { uuid: providerId },
                    },
                },
                customer: {
                    data: {
                        id: { uuid: customerId },
                    },
                },
            },
        } = tx;

        const { initiatedAs } = protectedData || {};

        if (!initiatedAs) {
            return acc;
        }
        const initiatedAsOwner = initiatedAs === userTypeHorseowner;
        const initatedAsRider = initiatedAs === userTypeRider;

        const isHorseowner = userType === userTypeHorseowner;

        const isProvider = providerId === currentUserId;
        const isCustomer = customerId === currentUserId;

        /**
         * tx is initiated by other party
         * current user becomes tx provider
         * initiated by other owner
         * current role is also owner
         * invalidRole
         */
        if (isProvider && initiatedAsOwner && isHorseowner) {
            return acc;
        }
        /**
         * tx is initiated by current user
         * current user becomes tx customer
         * tx initiated as rider
         * current role is onwner
         * invalidRole
         */
        if (isCustomer && initatedAsRider && isHorseowner) {
            return acc;
        }

        return [...acc, tx];
    }, []);

    return response;
};

export const loadData = (params = {}) => async (dispatch, getState) => {
    const { user } = getState();

    dispatch(fetchOrdersOrSalesRequest());

    try {
        const response = await dispatch(fetchTransactionsData(txDataApiQueryParams));
        const txData = filterTxByRole(response, ensureCurrentUser(user.currentUser));
        /**
         * transaction entities (incl. metadata.deletedMessages) must be in the
         * marketplaceData store before fetchInboxMessages resolves messages for them
         */
        dispatch(addMarketplaceEntities(txData));
        dispatch(fetchInboxMessages(txData.data.data));
        const transactions = await dispatch(fetchSubstitutedListings(txData.data.data));
        const transactionsData = {
            ...txData,
            data: {
                ...txData.data,
                data: transactions,
            },
        };

        dispatch(addMarketplaceEntities(transactionsData));
        dispatch(fetchOrdersOrSalesSuccess(transactionsData));
        dispatch(fetchAppointments(transactions));

        return transactionsData;
    } catch (e) {
        dispatch(fetchOrdersOrSalesError(storableError(e)));
        throw e;
    }
};

export const archieveChat = (txId, action = 'add') => (dispatch, getState, sdk) => {
    dispatch(requestArchievedTransactionData());

    const {
        user: { currentUser },
    } = getState();

    const {
        attributes: {
            profile: { protectedData },
        },
    } = currentUser;
    const isAddAction = action === 'add';
    const isRemoveAction = action === 'remove';

    const archievedTransactions = isAddAction
        ? {
              ...(protectedData.archievedTransactions || {}),
              [txId]: {
                  updatedAt: new Date().toString(),
              },
          }
        : {
              ...(protectedData.archievedTransactions || {}),
          };

    if (isRemoveAction) {
        delete archievedTransactions[txId];
    }

    const params = {
        protectedData: {
            archievedTransactions,
        },
    };

    const currentUserDataUpd = { ...currentUser };
    currentUserDataUpd.attributes.profile.protectedData.archievedTransactions = archievedTransactions;

    return dispatch(updateUserProfileInfo(params, currentUserDataUpd))
        .then(() => {
            dispatch(requestArchievedTransactionSuccess());
        })
        .catch(e => {
            dispatch(requestArchievedTransactionFailure(storableError(e)));
        });
};
